// theme.js

// Color palette
export const colors = {
  primary: {
    50: "#EBF5FF",
    100: "#E1EFFE",
    200: "#C3DAFE",
    300: "#A3BFFA",
    400: "#7F9CF5",
    500: "#667EEA",
    600: "#5A67D8",
    700: "#4C51BF",
    800: "#34399B",
    900: "#1E2156",
  },
  neutral: {
    50: "#FAFBFC",
    100: "#F4F5F7",
    200: "#E5E7EB",
    300: "#D2D6DC",
    400: "#9FA6B2",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#252F3F",
    900: "#161E2E",
  },
  success: {
    50: "#F0FDF4",
    500: "#22C55E",
    700: "#15803D",
  },
  warning: {
    50: "#FFFBEB",
    500: "#F59E0B",
    700: "#B45309",
  },
  error: {
    50: "#FEF2F2",
    500: "#EF4444",
    700: "#B91C1C",
  },
};

// Semantic aliases for theme consumption (add to theme object below)
export const semanticColors = {
  error: {
    ...colors.error,
    main: colors.error[500],
    dark: colors.error[700],
    light: colors.error[50],
  },
  success: {
    ...colors.success,
    main: colors.success[500],
    dark: colors.success[700],
    light: colors.success[50],
  },
  warning: {
    ...colors.warning,
    main: colors.warning[500],
    dark: colors.warning[700],
    light: colors.warning[50],
  },
  primary: {
    ...colors.primary,
    main: colors.primary[700],
    hover: colors.primary[800],
    light: colors.primary[100],
    contrastText: "#fff",
    surface: colors.primary[50],
  },
  accent: {
    main: colors.primary[700], // deep blue for accent
    hover: colors.primary[800],
    surface: colors.primary[50],
    contrastText: "#fff",
  },
  neutral: {
    ...colors.neutral,
  },
  surface: {
    default: "#fff",
    raised: "#F9FAFB",
    paper: "#fff",
  },
};

// Typography
export const typography = {
  fonts: {
    body: "Inter, system-ui, -apple-system, sans-serif",
    heading: "Inter, system-ui, -apple-system, sans-serif",
    monospace: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
};

// Spacing
export const spacing = {
  px: "1px",
  0: "0",
  0.5: "0.125rem",
  1: "0.25rem",
  1.5: "0.375rem",
  2: "0.5rem",
  2.5: "0.625rem",
  3: "0.75rem",
  3.5: "0.875rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  7: "1.75rem",
  8: "2rem",
  9: "2.25rem",
  10: "2.5rem",
  12: "3rem",
  14: "3.5rem",
  16: "4rem",
  20: "5rem",
  24: "6rem",
  28: "7rem",
  32: "8rem",
  36: "9rem",
  40: "10rem",
};

// Shadows
export const shadows = {
  sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
  md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
  xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
};

// Radii (THIS IS THE CRUCIAL MISSING SECTION)
export const radii = {
  none: "0px",
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  full: "9999px",
};

// Breakpoints
export const breakpoints = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

// Animations
export const animations = {
  durations: {
    fast: "150ms",
    normal: "200ms",
    slow: "300ms",
  },
  timings: {
    ease: "cubic-bezier(0.4, 0, 0.2, 1)",
    linear: "linear",
    "ease-in": "cubic-bezier(0.4, 0, 1, 1)",
    "ease-out": "cubic-bezier(0, 0, 0.2, 1)",
    "ease-in-out": "cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

export const theme = {
  colors: semanticColors, // <--- THIS IS WHAT YOU IMPORT
  typography,
  spacing,
  shadows,
  radii, // <--- ADDED, REQUIRED FOR BORDER RADIUS
  breakpoints,
  animations,
};

export default theme;
